import { Switch, SwitchProps } from '@mui/joy';
import { ICellRendererParams } from 'ag-grid-community';
import { useMemo } from 'react';

import { useAgGridCellUpdateContext } from 'core/states';
import { AgGridDataRow } from 'core/types';
import { getAgGridTableCellData } from 'core/utils';
import { Size, Variant } from 'theme/enums';

import { Loader } from './Loader';

interface IProps extends ICellRendererParams {
  colId: string;
  isEditable?: (data: AgGridDataRow) => boolean | boolean;
  switchProps?: SwitchProps;
}

export const SwitchCell = ({ colId, isEditable = () => true, switchProps, ...props }: IProps) => {
  const { isUpdating } = useAgGridCellUpdateContext();

  const isGroupedByColumn = colId === props?.node?.rowGroupColumn?.getColId();

  const data = getAgGridTableCellData(props, colId);

  const isCellEditable = useMemo(() => {
    if (typeof isEditable === 'function') {
      return isEditable(data);
    }

    return isEditable;
  }, [data, isEditable]);

  const isCellUpdating = isUpdating(props.node.id, colId);

  if (!data) {
    return null;
  }

  const { value, setValue } = props;

  const disabled = isGroupedByColumn || !isCellEditable || switchProps?.disabled;

  if (isCellUpdating) {
    return <Loader size={Size.SM} style={{ justifyContent: 'inherit', margin: 0 }} />;
  }

  return (
    <Switch
      checked={Boolean(value)}
      onChange={() => {
        if (!isCellEditable) {
          return;
        }

        setValue(!value);
      }}
      disabled={disabled}
      variant={disabled && !value ? Variant.OUTLINED : Variant.SOLID}
      {...switchProps}
    />
  );
};
